import type { Models } from "api/types";
import { storage } from "modules/storage";
import { currencyService } from "toolbox/CurrencyService";

import {
  requestTrackingAuthorization,
  requiresTrackingAuthorization,
} from "./adTrackingAuthorization";
import { hasOptedOutOfAdTracking } from "./adTrackingOptOut";

/** Loading the tags needs only the IDs; `isCanadian` decides the first-visit notice, not the tags. */
type AdTracking = Omit<NonNullable<Models<"PlatformModel">["adTracking"]>, "isCanadian">;

type PixelCall = (...args: unknown[]) => void;

interface MetaPixel extends PixelCall {
  queue: unknown[][];
  loaded: boolean;
  version: string;
  disablePushState: boolean;
  callMethod?: PixelCall;
  push: unknown;
}

declare global {
  interface Window {
    fbq?: MetaPixel;
    _fbq?: MetaPixel;
    gtag?: PixelCall;
    dataLayer?: unknown[];
  }
}

const META_SCRIPT_URL = "https://connect.facebook.net/en_US/fbevents.js";
const PURCHASE_REPORTED_KEY_PREFIX = "adPixelPurchaseReported:";
const GOOGLE_SCRIPT_URL = "https://www.googletagmanager.com/gtag/js";

/**
 * Query keys that are safe to leave on a page URL an ad pixel reads. Meta reads the full address
 * of the page with every event and cannot be told to drop the query, so a page carrying anything
 * else (a gift sender's name and phone, a Stripe client secret) reports nothing at all.
 */
const SAFE_QUERY_KEYS: ReadonlySet<string> = new Set([
  "promotion",
  "browseLocation",
  "languageCode",
  "utm_source",
  "utm_medium",
  "utm_campaign",
  "utm_term",
  "utm_content",
  "gclid",
  "fbclid",
  "shopDisplayOrderId",
  "orderTransactionId",
  "fromSignIn",
  "comingFromScan",
  "isMobileApp",
  "locationId",
  "packageId",
  "priceId",
  "membershipId",
  "giftCardListingId",
  "quantity",
  "sort",
]);

/**
 * The only property names an ad pixel ever receives, at the top level of a payload or inside one of
 * its `items`. Every outgoing payload is cut down to these before it is sent. Names are catalogue
 * names the practice published (a treatment, product, membership or gift card), never anything a
 * patient typed, answered or was told about their health, and never a person's name or contact.
 */
export const AD_PIXEL_PAYLOAD_KEYS: ReadonlySet<string> = new Set([
  /** Order or item price in the currency's major unit. */
  "value",
  /** ISO 4217 code of the practice's currency. */
  "currency",
  /** Meta: catalogue listing ids. */
  "content_ids",
  /** Meta: always `product`. */
  "content_type",
  /** Meta: the catalogue listing's published name. */
  "content_name",
  /** Meta: units added to the cart. */
  "num_items",
  /** Google: the order id a purchase conversion is deduplicated on. */
  "transaction_id",
  /** Google: the practice's conversion id and label. */
  "send_to",
  /** Google: the catalogue items an event is about, each cut down to the keys below. */
  "items",
  /** Google item: catalogue listing id. */
  "item_id",
  /** Google item: the catalogue listing's published name. */
  "item_name",
  /** Google item: units added to the cart. */
  "quantity",
  /** Google page view: origin and path only, never the query. */
  "page_location",
  "page_path",
  "send_page_view",
]);

/**
 * The payload keys a practice in limited data mode sends: no catalogue names, since a treatment's
 * name can say what a patient is being treated for. Ids, prices and quantities still go out.
 */
export const LIMITED_DATA_PAYLOAD_KEYS: ReadonlySet<string> = new Set(
  [...AD_PIXEL_PAYLOAD_KEYS].filter((key) => key !== "content_name" && key !== "item_name"),
);

type Payload = Record<string, unknown>;

export interface PurchaseEvent {
  orderId: string;
  /** In the currency's major unit, e.g. dollars. */
  value: number;
  currency: string;
}

export interface CheckoutEvent {
  /** Reported once per order, however often the cart reloads or checkout is re-entered. */
  orderId?: string;
  /** In the currency's major unit; unknown when checkout opens straight from a product. */
  value?: number;
  currency?: string;
}

export interface CatalogItem {
  id: string;
  /** The listing's published catalogue name. */
  name?: string;
  /** In the currency's major unit. */
  value?: number;
  currency?: string;
}

/** The patient's own contact details, used only to match their events to their ad account. */
export interface AdMatchingIdentity {
  email?: string | null;
  /** E.164, as the patient app stores it; any other shape is not sent. */
  phone?: string | null;
}

interface MatchingData {
  email?: string;
  phone?: string;
}

const E164_PHONE = /^\+[1-9]\d{10,14}$/;

export function toAllowedPayload(payload: Payload, allowedKeys: ReadonlySet<string>): Payload {
  return Object.fromEntries(
    Object.entries(payload)
      .filter(([key, value]) => allowedKeys.has(key) && value !== undefined)
      .map(([key, value]) => [
        key,
        Array.isArray(value)
          ? value.map((entry) =>
              entry !== null && typeof entry === "object"
                ? toAllowedPayload(entry as Payload, allowedKeys)
                : entry,
            )
          : value,
      ]),
  );
}

/** A backend price in whole minor units, as the ad pixels take it: major units in the practice's currency. */
export function adPrice(minorUnits: number): { value: number; currency: string } {
  const { code, decimalDigits } = currencyService.getCurrency();
  return { value: minorUnits / 10 ** decimalDigits, currency: code };
}

function toMatchingData({ email, phone }: AdMatchingIdentity): MatchingData | undefined {
  const normalizedEmail = email?.trim().toLowerCase();
  const normalizedPhone = phone?.trim();
  const data: MatchingData = {
    ...(normalizedEmail && normalizedEmail.includes("@") && { email: normalizedEmail }),
    ...(normalizedPhone && E164_PHONE.test(normalizedPhone) && { phone: normalizedPhone }),
  };
  return data.email === undefined && data.phone === undefined ? undefined : data;
}

/** Meta advanced matching: the pixel normalizes and SHA-256 hashes these before they leave the device. */
function metaMatching({ email, phone }: MatchingData): Record<string, string> {
  return {
    ...(email !== undefined && { em: email }),
    ...(phone !== undefined && { ph: phone.slice(1) }),
  };
}

/**
 * Google enhanced conversions: gtag hashes these before they leave the device, and takes a phone
 * number only alongside an email.
 */
function googleMatching({ email, phone }: MatchingData): Record<string, string> | undefined {
  if (email === undefined) return undefined;
  return { email, ...(phone !== undefined && { phone_number: phone }) };
}

function sameMatching(a: MatchingData | undefined, b: MatchingData | undefined): boolean {
  return a?.email === b?.email && a?.phone === b?.phone;
}

/** Whether `next` only adds to what a tag already holds, as a patient finishing their profile does. */
function addsTo(next: MatchingData | undefined, sent: MatchingData): boolean {
  return (
    next !== undefined &&
    (sent.email === undefined || next.email === sent.email) &&
    (sent.phone === undefined || next.phone === sent.phone)
  );
}

function present(id: string | null | undefined): id is string {
  return id !== null && id !== undefined && id.trim() !== "";
}

function hasOnlySafeQueryKeys(search: string): boolean {
  return [...new URLSearchParams(search).keys()].every((key) => SAFE_QUERY_KEYS.has(key));
}

/** Meta also reads the referrer with every event, so a page reached from an unsafe address reports nothing. */
function referrerSearch(): string {
  if (document.referrer === "") return "";
  // URL.canParse is missing from the iOS 16 WebView, so an unparseable referrer is caught instead.
  try {
    return new URL(document.referrer).search;
  } catch {
    return "";
  }
}

function isSafeLocation(): boolean {
  return hasOnlySafeQueryKeys(globalThis.location.search) && hasOnlySafeQueryKeys(referrerSearch());
}

function loadScript(src: string): void {
  const script = document.createElement("script");
  script.async = true;
  script.src = src;
  document.head.appendChild(script);
}

function installMetaPixel(pixelId: string, matching: MatchingData | undefined): void {
  const fbq = function (...args: unknown[]) {
    if (fbq.callMethod) fbq.callMethod(...args);
    else fbq.queue.push(args);
  } as MetaPixel;
  fbq.push = fbq;
  fbq.loaded = true;
  fbq.version = "2.0";
  fbq.queue = [];
  // The pixel's own history listener would send a PageView we do not control; ours is explicit.
  fbq.disablePushState = true;
  window.fbq = fbq;
  window._fbq = fbq;
  loadScript(META_SCRIPT_URL);
  // Stops the pixel scraping form fields and button text on its own.
  fbq("set", "autoConfig", false, pixelId);
  if (matching === undefined) fbq("init", pixelId);
  else fbq("init", pixelId, metaMatching(matching));
}

function installGoogleAds(adsId: string): void {
  window.dataLayer = window.dataLayer ?? [];
  // gtag.js reads the `arguments` object itself off the data layer, so a rest-args wrapper breaks it.
  window.gtag = function gtag() {
    // eslint-disable-next-line prefer-rest-params
    window.dataLayer!.push(arguments);
  };
  loadScript(`${GOOGLE_SCRIPT_URL}?id=${encodeURIComponent(adsId)}`);
  window.gtag("js", new Date());
  window.gtag("config", adsId, { send_page_view: false, allow_enhanced_conversions: true });
}

/**
 * Meta Pixel and Google Ads tags for the practice's own ad accounts. Nothing loads, and every
 * method is a no-op, until {@link configure} is given an id; a practice that set none loads no
 * third-party script at all.
 */
class AdPixels {
  private metaPixelId: string | undefined;
  private googleAdsId: string | undefined;
  private googleConversionLabel: string | undefined;
  private optedOutThisSession = false;
  private pageViewPending = false;
  private matching: MatchingData | undefined;
  /** Matching data a loaded tag was given; a tag cannot be made to forget it before the page reloads. */
  private sentMatching: MatchingData | undefined;
  private identitySwitched = false;
  /** Set once a practice in limited data mode configures the tags, and never cleared. */
  private limitedData = false;
  private readonly checkoutReportedOrderIds = new Set<string>();

  configure(adTracking: AdTracking | null | undefined): void {
    if (adTracking === null || adTracking === undefined) return;
    if (adTracking.limitedData) {
      this.limitedData = true;
      // The app identifies the patient before it configures, so a pending identity is dropped here.
      this.matching = undefined;
    }
    if (this.isOptedOut()) return;
    const { metaPixelId, googleAdsId } = adTracking;
    if (!present(metaPixelId) && !present(googleAdsId)) return;

    if (!requiresTrackingAuthorization()) {
      this.load(adTracking);
      return;
    }
    void requestTrackingAuthorization()
      .then((authorized) => {
        if (authorized) this.load(adTracking);
      })
      .catch(() => console.warn("Ad tags failed to load."));
  }

  private load(adTracking: AdTracking): void {
    if (this.isOptedOut()) return;
    const { metaPixelId, googleAdsId, googleAdsPurchaseConversionLabel } = adTracking;

    if (present(metaPixelId) && this.metaPixelId === undefined) {
      this.metaPixelId = metaPixelId.trim();
      installMetaPixel(this.metaPixelId, this.matching);
    }
    if (present(googleAdsId) && this.googleAdsId === undefined) {
      this.googleAdsId = googleAdsId.trim();
      installGoogleAds(this.googleAdsId);
      this.setGoogleMatching();
    }
    if (present(googleAdsPurchaseConversionLabel)) {
      this.googleConversionLabel = googleAdsPurchaseConversionLabel.trim();
    }
    if (this.matching !== undefined) this.sentMatching = this.matching;
    // On iOS the tags load only after the tracking prompt is answered, after the first page rendered.
    if (this.pageViewPending) {
      this.pageViewPending = false;
      this.pageView();
    }
  }

  get isActive(): boolean {
    return this.metaPixelId !== undefined || this.googleAdsId !== undefined;
  }

  /**
   * The signed-in patient's email and phone, sent only to a tag that loaded, so only past every
   * opt-out, Global Privacy Control and App Tracking Transparency check. Set before
   * {@link configure}, they go out with the tags' first load. Nothing is kept or sent for a
   * practice in limited data mode.
   */
  identify(identity: AdMatchingIdentity): void {
    if (this.isOptedOut() || this.limitedData) return;
    const matching = toMatchingData(identity);
    if (sameMatching(matching, this.matching)) return;
    if (this.sentMatching !== undefined && !addsTo(matching, this.sentMatching)) {
      this.forgetIdentity();
      return;
    }
    this.identitySwitched = false;
    this.matching = matching;
    if (!this.isActive || matching === undefined) return;
    if (this.metaPixelId !== undefined)
      window.fbq?.("init", this.metaPixelId, metaMatching(matching));
    this.setGoogleMatching();
    this.sentMatching = matching;
  }

  /**
   * Drops the signed-out patient's details. A tag that already received them keeps them until the
   * page reloads, so nothing more is reported from this page after that.
   */
  forgetIdentity(): void {
    this.matching = undefined;
    if (this.sentMatching !== undefined) this.identitySwitched = true;
  }

  private setGoogleMatching(): void {
    if (this.googleAdsId === undefined || window.gtag === undefined) return;
    const userData = this.matching === undefined ? undefined : googleMatching(this.matching);
    if (userData !== undefined) window.gtag("set", "user_data", userData);
  }

  /**
   * Stops every further event now and withdraws consent on a tag that already loaded. The scripts
   * cannot be unloaded, so opting back in takes effect on the next page load.
   */
  optOut(): void {
    this.optedOutThisSession = true;
    this.matching = undefined;
    window.fbq?.("consent", "revoke");
    window.gtag?.("consent", "update", {
      ad_storage: "denied",
      ad_user_data: "denied",
      ad_personalization: "denied",
      analytics_storage: "denied",
    });
  }

  pageView(): void {
    if (!this.isActive) {
      this.pageViewPending = true;
      return;
    }
    if (!this.isReady()) return;
    const { origin, pathname } = globalThis.location;
    this.meta("PageView");
    this.google("event", "page_view", {
      page_location: `${origin}${pathname}`,
      page_path: pathname,
    });
  }

  viewContent({ id, name, value, currency }: CatalogItem): void {
    if (!this.isReady()) return;
    this.meta("ViewContent", {
      content_ids: [id],
      content_type: "product",
      content_name: name,
      value,
      currency,
    });
    this.google("event", "view_item", {
      value,
      currency,
      items: [{ item_id: id, item_name: name }],
    });
  }

  addToCart({ id, name, value, currency }: CatalogItem, quantity: number): void {
    if (!this.isReady()) return;
    this.meta("AddToCart", {
      content_ids: [id],
      content_type: "product",
      content_name: name,
      num_items: quantity,
      value,
      currency,
    });
    this.google("event", "add_to_cart", {
      value,
      currency,
      items: [{ item_id: id, item_name: name, quantity }],
    });
  }

  /** A new patient finished signing up to the practice. */
  completeRegistration(): void {
    if (!this.isReady()) return;
    this.meta("CompleteRegistration");
    this.google("event", "sign_up", {});
  }

  /** An appointment slot was reserved with the practice's booking system. */
  schedule({ id, name }: CatalogItem): void {
    if (!this.isReady()) return;
    this.meta("Schedule", { content_ids: [id], content_type: "product", content_name: name });
  }

  initiateCheckout({ orderId, value, currency }: CheckoutEvent = {}): void {
    if (!this.isReady()) return;
    if (orderId !== undefined) {
      if (this.checkoutReportedOrderIds.has(orderId)) return;
      this.checkoutReportedOrderIds.add(orderId);
    }
    this.meta("InitiateCheckout", { value, currency });
    this.google("event", "begin_checkout", { value, currency });
  }

  /** Reports a paid order once per order id, however often the success page renders or reloads. */
  purchase({ orderId, value, currency }: PurchaseEvent): void {
    if (!this.isReady()) return;
    const reportedKey = `${PURCHASE_REPORTED_KEY_PREFIX}${orderId}`;
    if (storage.getItem(reportedKey) !== null) return;
    storage.setItem(reportedKey, "1");
    this.meta("Purchase", { value, currency }, { eventID: orderId });
    if (this.googleConversionLabel !== undefined) {
      this.google("event", "conversion", {
        send_to: `${this.googleAdsId}/${this.googleConversionLabel}`,
        value,
        currency,
        transaction_id: orderId,
      });
    }
  }

  private isOptedOut(): boolean {
    return this.optedOutThisSession || hasOptedOutOfAdTracking();
  }

  private isReady(): boolean {
    return this.isActive && !this.identitySwitched && !this.isOptedOut() && isSafeLocation();
  }

  private meta(event: string, payload?: Payload, options?: { eventID: string }): void {
    if (this.metaPixelId === undefined || window.fbq === undefined) return;
    if (payload === undefined) window.fbq("track", event);
    else if (options === undefined) window.fbq("track", event, this.allowed(payload));
    else window.fbq("track", event, this.allowed(payload), options);
  }

  private google(command: string, event: string, payload: Payload): void {
    if (this.googleAdsId === undefined || window.gtag === undefined) return;
    window.gtag(command, event, this.allowed(payload));
  }

  private allowed(payload: Payload): Payload {
    return toAllowedPayload(
      payload,
      this.limitedData ? LIMITED_DATA_PAYLOAD_KEYS : AD_PIXEL_PAYLOAD_KEYS,
    );
  }
}

export const adPixels = new AdPixels();
